import React, { useState, useEffect, useCallback, useRef } from "react";
import {useNavigate} from "react-router-dom";
import { createPortal } from "react-dom";
import {
  Search,
  Filter,
  Calendar,
  Clock,
  Tag,
  X,
  ChevronLeft,
  Loader2,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import Pop from "../components/PopupForm";
import api from "../services/api";
import {
  Menu,
  ChevronDown,
  Phone,
  Globe,
  Users,
  BookOpen,
  ClipboardCheck,
  CreditCard,
  FileText,
  Plane,
  Home,
  MapPin,
  ArrowRight,
} from "lucide-react";
const BlogPage = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");

  const resultsRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [postsRes, catsRes] = await Promise.all([
        api.get("/posts"),
        api.get("/categories"),
      ]);
      setBlogs(Array.isArray(postsRes.data) ? postsRes.data : []);
      setCategories(Array.isArray(catsRes.data) ? catsRes.data : []);
    } catch (err) {
      setError(err.response?.data?.message || "Backend server unreachable.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCategoryChange = (slug) => {
    setActiveCategory(slug);
    if (resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleOpenBlog = (blog) => {
    setSelectedBlog(blog);
    window.history.pushState(null, "", `/blogs/${blog.slug}`);
  };

  const handleCloseBlog = () => {
    setSelectedBlog(null);
    window.history.pushState(null, "", "/blogs");
  };

  useEffect(() => {
    const path = window.location.pathname;
    if (path.includes("/blogs/") && blogs.length > 0) {
      const slug = path.split("/").pop();
      const foundBlog = blogs.find((b) => b.slug === slug);
      if (foundBlog) setSelectedBlog(foundBlog);
    }
  }, [blogs]);

  const getImageUrl = (blog) => {
    const path = blog.featuredImage?.url;
    if (!path)
      return "https://images.unsplash.com/photo-1432821596592-e2c18b78144f?q=80&w=1000&auto=format&fit=crop";
    if (path.startsWith("http")) return path;
    const baseUrl =
      process.env.REACT_APP_API_URL?.replace("/api", "") ||
      "http://localhost:5000";
    return `${baseUrl}${path}`;
  };

  const filteredBlogs = blogs.filter((blog) => {
    const matchesSearch = blog.title
      ?.toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesCategory =
      activeCategory === "All" ||
      blog.categories?.some((cat) => {
        const catSlug = typeof cat === "object" ? cat.slug : null;
        const catName = typeof cat === "object" ? cat.name : null;
        return catSlug === activeCategory || catName === activeCategory;
      });
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-white font-sans mb-5 pb-20 text-gray-900">
      <header className="bg-[#0a328a] text-white pt-20 pb-20 relative overflow-hidden">
        <div className="container mx-auto px-6 text-center">
          <nav className="text-blue-200 text-sm mb-3">Home › Blogs</nav>
          <h1 className="text-6xl font-black mb-6 tracking-tight italic">
            Insights
          </h1>
        </div>
      </header>

      <main className="container mx-auto px-6  relative z-20">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 text-blue-700">
            <Loader2 className="animate-spin mb-4" size={50} />
            <p className="font-bold tracking-widest uppercase text-xs text-gray-400">
              Loading...
            </p>
          </div>
        ) : error ? (
          <div className="bg-white border border-red-100 p-12 rounded-[2.5rem] text-center max-w-2xl mx-auto shadow-xl">
            <AlertCircle className="mx-auto text-red-500 mb-6" size={64} />
            <h3 className="text-2xl font-black text-gray-900 mb-2">Error</h3>
            <p className="text-gray-500 mb-8">{error}</p>
            <button
              onClick={fetchData}
              className="bg-[#0a328a] text-white px-10 py-4 rounded-2xl font-bold"
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 mt-20 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {filteredBlogs.map((blog) => (
              <BlogCard
                key={blog._id}
                blog={blog}
                onClick={() => navigate(`/blogs/${blog.slug}`)}
                imageUrl={getImageUrl(blog)}
              />
            ))}
          </div>
        )}
      </main>

      {selectedBlog && (
        <DetailOverlay
          blog={selectedBlog}
          blogs={blogs}
          categories={categories}
          onClose={handleCloseBlog}
          imageUrl={getImageUrl(selectedBlog)}
          onOpenBlog={handleOpenBlog}
          onCategoryClick={handleCategoryChange}
        />
      )}
    </div>
  );
};

const BlogCard = ({ blog, onClick, imageUrl }) => (
  <div
    onClick={onClick}
    className="bg-white rounded-[2.5rem] overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500 border border-gray-100 flex flex-col cursor-pointer group hover:-translate-y-2"
  >
    <div className="relative h-72 w-full overflow-hidden">
      <img
        src={imageUrl}
        alt={blog.title}
        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
        onError={(e) =>
          (e.target.src =
            "https://images.unsplash.com/photo-1432821596592-e2c18b78144f")
        }
      />
    </div>
    <div className="p-8 flex flex-col flex-grow">
      <span className="text-blue-600 text-[10px] font-black uppercase tracking-[0.3em] mb-4">
        {blog.categories?.[0]?.name || "Article"}
      </span>
      <h3 className="text-2xl font-extrabold text-gray-900 mb-4 leading-tight group-hover:text-[#0a328a] transition-colors line-clamp-2 italic">
        {blog.title}
      </h3>
      <p className="text-gray-500 text-sm leading-relaxed mb-8 line-clamp-3 font-medium">
        {blog.description || "Read more about our latest updates..."}
      </p>
      <div className="mt-auto pt-6 border-t border-gray-100 flex items-center justify-between text-gray-400 text-[10px] font-bold uppercase tracking-widest">
        <div className="flex items-center gap-2">
          <Calendar size={14} className="text-blue-500" />
          <span>{new Date(blog.createdAt).toLocaleDateString()}</span>
        </div>
        <div className="flex items-center gap-2">
          <span>View Article</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-arrow-right-icon lucide-arrow-right"
          >
            <path d="M5 12h14" />
            <path d="m12 5 7 7-7 7" />
          </svg>
        </div>
      </div>
    </div>
  </div>
);

const DetailOverlay = ({
  blog,
  blogs,
  categories,
  onClose,
  imageUrl,
  onOpenBlog,
  onCategoryClick,
}) => {
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [date, setDate] = useState("");
  const [message, setMessage] = useState("");

const modalRoot = document.getElementById("modal-root");

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formData = {
      blogId: blog._id,
      name,
      email,
      phone,
      address,
      date,
      message,
    };

    try {
      await api.post("/comments", formData);

      alert("Comment submitted successfully");
    } catch (error) {
      alert("Failed to submit comment");
    }
  };

  // Body scroll lock logic
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;
    const scrollBarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    if (scrollBarWidth > 0) {
      document.body.style.paddingRight = `${scrollBarWidth}px`;
    }
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;
    };
  }, []);

  if (!modalRoot) return null;

  
    return createPortal(
    <div className="fixed inset-0 z-[100] p-24 bg-gray-50 overflow-y-auto">
      {/* Top Header */}
      <div className="bg-white border-b p-4 flex justify-between items-center sticky top-0 z-50">
        <button
          onClick={() => {
            onClose();
            window.history.pushState(null, "", "/blogs");
          }}
          className="flex items-center gap-2 text-[#0a328a] font-semibold hover:underline"
        >
          <ChevronLeft size={18} />
          Back to Blogs
        </button>
      </div>

      {/* Hero Banner */}
      <div className="bg-[#0a328a] text-white py-16 text-center">
        <h1 className="text-4xl font-bold tracking-wide">BLOG DETAILS</h1>
        <p className="text-sm text-blue-200 mt-2">Home › Blog Details</p>
      </div>

      {/* Main Container */}
      <div className="max-w-7xl mx-auto px-6 mb-10 py-14 grid grid-cols-1 lg:grid-cols-3 gap-10">
        {/* LEFT SIDE BLOG */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow p-6">
          <img
            src={imageUrl}
            className="w-full aspect-[1000/2000] object-cover rounded-lg mb-6 bg-gray-100"
            alt=""
          />

          <h2 className="text-2xl font-bold mb-3">{blog.title}</h2>

          <p className="text-gray-400 text-sm mb-6">
            {new Date(blog.createdAt).toDateString()}
          </p>

          <div
            className="prose max-w-none"
            dangerouslySetInnerHTML={{ __html: blog.content }}
          />

          {/* Leave A Comment */}
          <div className="mt-16 bg-white border rounded-xl p-8 shadow-sm">
            <h3 className="text-2xl font-bold mb-8">Leave A Comment</h3>

            <form
              onSubmit={handleSubmit}
              className="grid grid-cols-1 md:grid-cols-2 gap-6"
            >
              <input
                type="text"
                placeholder="Your Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />

              <input
                type="email"
                placeholder="Your Email"
                className="bg-gray-100 rounded-full px-5 py-3 outline-none"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />

              <input
                type="text"
                placeholder="Your Phone"
                className="bg-gray-100 rounded-full px-5 py-3 outline-none"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />

              <input
                type="date"
                className="bg-gray-100 rounded-full px-5 py-3 outline-none"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />

              <input
                type="text"
                placeholder="Your Address"
                className="bg-gray-100 rounded-full px-5 py-3 outline-none md:col-span-2"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />

              <textarea
                placeholder="Type your message"
                rows="5"
                className="bg-gray-100 rounded-2xl px-5 py-3 outline-none md:col-span-2"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              ></textarea>

              <div className="md:col-span-2 flex justify-center mt-4">
                <button
                  type="submit"
                  className="flex items-center gap-3 bg-[#0a328a] text-white px-10 py-3 rounded-full font-semibold hover:bg-blue-900 transition"
                >
                  Send Message
                  <ArrowRight size={18} />
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* RIGHT SIDEBAR */}
        <div className="space-y-8">
          {/* Categories */}
          <div className="bg-white p-5 rounded-xl shadow">
            <h3 className="font-bold mb-4">Categories</h3>

            <ul className="space-y-3 text-sm">
              {categories.map((cat) => (
                <li
                  key={cat._id}
                  onClick={() => {
                    onClose();
                    onCategoryClick(cat.slug);
                  }}
                  className="flex justify-between cursor-pointer hover:text-[#0a328a]"
                >
                  <span>{cat.name}</span>
                  <span className="text-gray-400">({cat.postCount})</span>
                </li>
              ))}
            </ul>
          </div>
          {/* Recent Posts */}
          <div className="bg-white p-5 rounded-xl shadow">
            <h3 className="font-bold mb-4">Recent Posts</h3>

            <div className="space-y-4">
              {blogs.slice(0, 3).map((post) => (
                <div
                  key={post._id}
                  onClick={() => onOpenBlog(post)}
                  className="flex gap-3 cursor-pointer group"
                >
                  <img
                    src={post.featuredImage?.url || imageUrl}
                    className="w-16 h-16 object-cover rounded"
                    alt=""
                  />

                  <div>
                    <p className="text-sm font-semibold group-hover:text-[#0a328a] line-clamp-2">
                      {post.title}
                    </p>

                    <span className="text-xs text-gray-400">
                      {new Date(post.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tags */}
          <div className="bg-white p-5 rounded-xl shadow">
            <h3 className="font-bold mb-3">Tag Cloud</h3>

            <div className="flex flex-wrap gap-2">
              {["Visa", "Study", "Immigration", "Travel"].map((tag) => (
                <span
                  key={tag}
                  className="text-xs bg-gray-100 px-3 py-1 rounded"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* CTA */}
        
      </div>
     <div className="w-[60em] h-[15em] m-10 mt-12 mb-16 p-20 mx-auto bg-gradient-to-br from-blue-700 to-[#0a328a] rounded-xl text-white flex flex-col md:flex-row items-center gap-8">
          <div>
            <h3 className="text-xl font-bold">
              Expert Guidance at Your Doorstep
            </h3>

            <p className="text-blue-100 text-sm">
              Book a personalized counseling session at home.
            </p>
          </div>

          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 bg-white text-[#0a328a] px-5 py-3 rounded-lg font-semibold"
          >
            <Phone size={16} />
            Door Step Counselling
          </button>

          <Pop show={showForm} onClose={() => setShowForm(false)} />
        </div>
    </div>,
    modalRoot
  )
};

export default BlogPage;
